import type { FormField } from '@kanbu/schema/contracts';
import { Trans } from '@lingui/react/macro';
import { Button } from '@utima/ui';
import { useRef } from 'react';

import { FormFieldRow } from './FormFieldRow';
import {
  createEmptyField,
  MAX_CLIENT_SIDE_FORM_FIELDS,
} from '../lib/formFieldDefaults';

export interface FormFieldListProps {
  fields: FormField[];
  savedKeys?: string[];
  onChange: (fields: FormField[]) => void;
}

export function FormFieldList({
  fields,
  savedKeys = [],
  onChange,
}: FormFieldListProps) {
  const identities = useRef(
    new WeakMap<FormField, { id: string; locked: boolean }>(),
  );
  const identity = (field: FormField) => {
    let value = identities.current.get(field);
    if (!value) {
      value = {
        id: crypto.randomUUID(),
        locked: savedKeys.includes(field.name),
      };
      identities.current.set(field, value);
    }

    return value;
  };
  const atMax = fields.length >= MAX_CLIENT_SIDE_FORM_FIELDS;

  const updateField = (index: number, field: FormField) => {
    identities.current.set(field, identity(fields[index]));
    onChange(fields.map((item, i) => (i === index ? field : item)));
  };

  const removeField = (index: number) => {
    onChange(fields.filter((_, i) => i !== index));
  };

  const duplicateField = (index: number) => {
    if (atMax) {
      return;
    }

    const source = fields[index];
    const copy = createEmptyField({
      ...source,
      name: `${source.name}_copy`.slice(0, 60),
      label: `${source.label} (copy)`,
    });

    onChange([...fields.slice(0, index + 1), copy, ...fields.slice(index + 1)]);
  };

  const moveField = (index: number, direction: -1 | 1) => {
    const target = index + direction;

    if (target < 0 || target >= fields.length) {
      return;
    }

    const next = [...fields];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div className='space-y-3'>
      <div className='flex items-center justify-between'>
        <span className='text-sm font-medium'>
          <Trans>Fields</Trans>{' '}
          <span className='text-muted-fg'>
            ({fields.length}/{MAX_CLIENT_SIDE_FORM_FIELDS})
          </span>
        </span>
        <Button
          size='sm'
          variant='secondary'
          disabled={atMax}
          onClick={() => onChange([...fields, createEmptyField()])}
        >
          <Trans>Add field</Trans>
        </Button>
      </div>

      {fields.map((field, index) => (
        <FormFieldRow
          key={identity(field).id}
          fieldKeyLocked={identity(field).locked}
          field={field}
          index={index}
          total={fields.length}
          onChange={next => updateField(index, next)}
          onRemove={() => removeField(index)}
          onDuplicate={() => duplicateField(index)}
          onMoveUp={() => moveField(index, -1)}
          onMoveDown={() => moveField(index, 1)}
        />
      ))}
    </div>
  );
}
